import { CircleInfoIcon } from "@xola/icons";
import { Currency, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { getAddonClientMax, getSoldOutLimitError } from "common";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { AddOn, CatalogItemDisplaySection, CatalogTemplateItem, LineItem, ProductCatalogItem } from "types";
import { getCatalogItemPhoto } from "../../../models/Catalog";
import { AddonLineItem, CatalogLineItem, useCreatePurchaseStore } from "../../../stores/createPurchaseStore";
import { CatalogItemImage } from "../../Common/CatalogItemImage";
import { NoTranslate } from "../../Common/NoTranslate";
import { QuantitySpinner } from "../../Common/QuantitySpinner/QuantitySpinner";
import { AddOnLimitOverrideSwitch } from "../AddOnLimitOverrideSwitch";
import { getSeller } from "@/utils/getSeller";

interface QuantityAddonProps {
    shouldShowDefaultImg?: boolean;
    shouldDecreaseOnly?: boolean;
    isPastDated?: boolean;
    isOverriding?: boolean;
    shouldAllowSavedQuantity?: boolean;
    catalog: AddOn;
    templateItem: CatalogTemplateItem;
    lineItems: LineItem[] | CatalogLineItem[] | AddonLineItem[];
    purchaseId?: ID;
    guestCount?: number;
    limitError?: string;
    /** The most this item may hold under the per event limit at the last Reserve, its own units included. */
    catalogItemOpen?: number;
    onChange: (value: number, id: ID | undefined) => void;
    onOverrideChange?: (value: boolean) => void;
}

export const QuantityAddon = ({
    shouldShowDefaultImg = true,
    shouldDecreaseOnly = false,
    isPastDated = false,
    isOverriding = false,
    shouldAllowSavedQuantity = false,
    catalog,
    templateItem,
    lineItems,
    purchaseId,
    guestCount = 0,
    limitError,
    catalogItemOpen,
    onChange,
    onOverrideChange,
}: QuantityAddonProps) => {
    const lineItem = (lineItems as CatalogLineItem[]).find((lineItem) => lineItem?.template?.id === templateItem.id);
    const purchaseIdFromStore = useCreatePurchaseStore((state) => state.purchaseId);
    const disabledSpinner = !purchaseId && !purchaseIdFromStore;
    const value = lineItem?.quantity ?? 0;
    // The API skips limits for a past arrival, so the client does too.
    const clientMax = isPastDated
        ? undefined
        : getAddonClientMax({ isOverriding, catalogItem: catalog, guestCount, catalogItemOpen });
    // A saved quantity can be above a limit lowered later, or overridden when booked. The API checks only increases, so
    // the saved quantity is the lowest cap.
    const max = shouldAllowSavedQuantity && clientMax !== undefined ? Math.max(clientMax, value) : clientMax;
    const isSoldOut = catalogItemOpen === 0 && max === 0 && value > 0;
    const shownLimitError = getSoldOutLimitError(limitError, isSoldOut);

    const handleChange = (value: number) => {
        onChange(value, templateItem?.id);
    };

    return (
        <QuantityAddonItem
            key={catalog.id}
            shouldShowDefaultImg={shouldShowDefaultImg}
            disabledSpinner={disabledSpinner}
            shouldDecreaseOnly={shouldDecreaseOnly}
            isOverriding={isOverriding}
            catalogItem={catalog}
            value={value}
            max={max}
            limitError={shownLimitError}
            onChange={handleChange}
            onOverrideChange={onOverrideChange}
        />
    );
};

interface QuantityAddonItemProps {
    shouldShowDefaultImg?: boolean;
    disabledSpinner: boolean;
    shouldDecreaseOnly: boolean;
    isOverriding?: boolean;
    isLimitExceeded?: boolean;
    catalogItem: ProductCatalogItem;
    value: number;
    max?: number;
    error?: string;
    limitError?: string;
    onChange: (value: number) => void;
    onOverrideChange?: (value: boolean) => void;
}

export const QuantityAddonItem = ({
    shouldShowDefaultImg,
    disabledSpinner,
    shouldDecreaseOnly,
    isOverriding = false,
    isLimitExceeded,
    catalogItem,
    value: initialValue,
    max,
    error,
    limitError,
    onChange,
    onOverrideChange,
}: QuantityAddonItemProps) => {
    const seller = getSeller();
    const [value, setValue] = useState(initialValue);
    const isFormAddon = catalogItem.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE);
    const photoSource = getCatalogItemPhoto(catalogItem);
    const isDefaultProductImgVisible = Boolean(photoSource) || Boolean(shouldShowDefaultImg);
    const rowPadding = getAddonRowPadding(isDefaultProductImgVisible, isFormAddon);
    const isOverCap = !shouldDecreaseOnly && max !== undefined && value > max;
    const limitMessage = limitError ?? (isOverCap ? `Quantity cannot exceed ${max}` : undefined);

    const handleChange = (value: number) => {
        setValue(value);
        onChange(value);
    };

    return (
        <div data-testid="quantity-addon-item" className={clsx("flex w-full items-center", rowPadding)}>
            {isDefaultProductImgVisible && (
                <CatalogItemImage catalogItem={catalogItem} size={isFormAddon ? "small" : "medium"} />
            )}
            <div className="w-full flex-1">
                <div className="flex items-center space-x-2 pr-2">
                    <AddonNameField
                        isFormAddon={isFormAddon}
                        isError={Boolean(limitMessage)}
                        error={error}
                        catalogItem={catalogItem}
                    />
                    {catalogItem?.caption && (
                        <Tooltip content={catalogItem.caption}>
                            <CircleInfoIcon className="text-gray-400 cursor-pointer hover:text-black" />
                        </Tooltip>
                    )}
                </div>

                <div className={clsx("text-gray-darker")}>
                    {!isEmpty(catalogItem.prices) && (
                        <>
                            <var data-var="addonPrice">
                                <Currency currency={seller.currency}>{catalogItem.prices?.price?.min}</Currency>
                            </var>
                            /add-on
                        </>
                    )}
                </div>
            </div>

            <div className="relative flex items-center justify-center space-x-4 pr-1">
                <AddOnLimitOverrideSwitch
                    isLimitExceeded={isLimitExceeded}
                    limitOverride={{ limitError: limitMessage, isOverriding, setIsOverriding: onOverrideChange }}
                    restrictions={catalogItem.restrictions ?? []}
                />
                <QuantitySpinner
                    lockAtMax={shouldDecreaseOnly}
                    size="large"
                    classNames={isFormAddon ? { label: "mx-1" } : undefined}
                    max={shouldDecreaseOnly ? value : max}
                    total={value}
                    disabled={disabledSpinner}
                    value={value}
                    onChange={handleChange}
                />
            </div>
        </div>
    );
};

/** A medium image already spaces the rows apart; without it the limit line needs the extra padding. */
export const getAddonRowPadding = (isDefaultProductImgVisible: boolean, isFormAddon: boolean) => {
    return isDefaultProductImgVisible && !isFormAddon ? "py-2" : "py-4";
};

interface AddonNameFieldProps {
    isFormAddon: boolean;
    /** Shows the name in red without a message, for a limit error shown on its own row. */
    isError?: boolean;
    error?: string;
    catalogItem: ProductCatalogItem;
}

export const AddonNameField = ({ isFormAddon, isError = false, error, catalogItem }: AddonNameFieldProps) => {
    return (
        <NoTranslate
            as="div"
            className={clsx(
                "font-bold text-black",
                isFormAddon ? "text-sm" : "text-base",
                (isError || (isFormAddon && error)) && "text-red",
            )}
        >
            {`${catalogItem.name}${error ? ` ${error}` : ""}`}
        </NoTranslate>
    );
};
